import { isEqual } from "lodash";

import { DateTime } from "@bps/date-time";
import { StandardRangesOptionsKeys } from "@bps/fluent-ui";
import { Permission } from "@libs/gateways/core/CoreGateway.dtos.ts";
import { useStores } from "@stores/hooks/useStores.ts";
import { FilterBar } from "@ui-components/filter-bar/FilterBar.tsx";
import { FilterBarProps } from "@ui-components/filter-bar/FilterBar.types.ts";
import { usePatientLabel } from "@ui-components/hooks/usePatientLabel.ts";

import {
  defaultRecentPatientFilter,
  RecentPatientFilterKeys,
  recentPatientFilterOptions
} from "./RecentPatientsFilter.types.ts";

type RecentPatientsFilterProps = Pick<
  FilterBarProps<RecentPatientsFilterState>,
  "children"
>;

export interface RecentPatientsFilterState {
  userIds?: string[];
  locationIds?: string[];
  patientIds?: string[];
  eventCode?: string;
  eventTimeFrom?: Date;
  eventTimeTo?: Date;
  incompleteNotesOnly?: true;
}

export const RecentPatientsFilter: React.FC<RecentPatientsFilterProps> = ({
  children
}) => {
  const { core, practice, routing } = useStores();
  const location = routing.location;
  const recentPatientsFilter = location?.state?.filter as
    | RecentPatientsFilterState
    | undefined;

  const defaultPatientName = recentPatientsFilter?.patientIds?.[0]
    ? practice.contactsMap.get(recentPatientsFilter.patientIds[0])?.reversedName
    : undefined;

  const defaultValuesWithUserId: RecentPatientsFilterState = {
    ...defaultRecentPatientFilter,
    userIds: [core.userId]
  };

  const items: FilterBarProps<RecentPatientsFilterState>["items"] = [
    {
      type: "contactPicker",
      name: "patientIds",
      stickItem: true,
      props: {
        inputProps: {
          id: "recent-patients-search",
          placeholder: `Search for ${usePatientLabel(false)}`,
          defaultVisibleValue: defaultPatientName
        },
        styles: { root: { maxWidth: 250, minWidth: 250 } }
      }
    },
    {
      type: "optionsSelect",
      name: "eventCode",
      props: {
        id: "recent-patients-events",
        hideSearchOption: true,
        options: recentPatientFilterOptions,
        placeholder: "Event",
        calloutWidth: 200
      }
    }
  ];

  if (core.hasPermissions(Permission.RecentPatientsAdminAllowed)) {
    items.push({
      type: "usersSelect",
      name: "userIds",
      props: {
        id: "recent-patients-users",
        styles: { root: { width: 200 } },
        placeholder: "Users",
        multiSelect: true
      }
    });

    if (core.hasMultipleActiveLocations) {
      items.push({
        type: "locationsSelect",
        name: "locationIds",
        props: {
          id: "recent-patients-locations",
          styles: { root: { width: 200 } },
          placeholder: "Location",
          multiSelect: false
        }
      });
    }
  }

  items.push({
    type: "datesRangePicker",
    name: "datesRangePicker",
    props: {
      placeholder: "Dates",
      startDateProps: {
        minDate: DateTime.today().minus({ days: 7 }).toJSDate(),
        maxDate: DateTime.jsDateNow()
      },
      endDateProps: {
        minDate: DateTime.today().minus({ days: 7 }).toJSDate(),
        maxDate: DateTime.jsDateNow()
      },
      startDateName: "eventTimeFrom",
      endDateName: "eventTimeTo",
      standardRanges: [
        StandardRangesOptionsKeys.today,
        StandardRangesOptionsKeys.yesterday
      ]
    }
  });

  const isResetBtnDisabled = (values: RecentPatientsFilterState) =>
    isEqual(values, defaultValuesWithUserId);

  return (
    <FilterBar<RecentPatientsFilterState>
      items={items}
      initialValues={{
        userIds: [core.userId],
        ...recentPatientsFilter
      }}
      presets={[
        {
          text: "Incomplete notes",
          name: "incompleteNotesOnly",
          id: "recent-patients-incomplete",
          iconName: "BpDocEdit",
          tooltip: "Only show Incomplete notes",
          tooltipPresses: "Only showing Incomplete notes",
          valuesToBeSetOnToggleOn: {
            eventCode: RecentPatientFilterKeys.ConsultIncomplete
          },
          valuesToBeSetOnToggleOff: {
            eventCode: undefined
          }
        }
      ]}
      customResetButtonCondition={(_actions, values) =>
        isResetBtnDisabled(values)
      }
      onReset={actions => {
        actions.reset(defaultValuesWithUserId);
      }}
    >
      {children}
    </FilterBar>
  );
};
